import { useState } from 'react'
import { Badge, LoadingSpinner } from '../shared/components/ui'
import type { DeviceAccessInfo, GuestAccessStatus } from '../types/guest'

interface DeviceAccessItemProps {
  device: DeviceAccessInfo
  userStatus: GuestAccessStatus
  onToggle: (deviceId: number, grant: boolean) => Promise<void>
}

export default function DeviceAccessItem({ device, userStatus, onToggle }: DeviceAccessItemProps) {
  const [isLoading, setIsLoading] = useState(false)

  const isDisabled = userStatus === 'pending' || userStatus === 'revoked'

  const handleToggle = async () => {
    if (isDisabled) return

    setIsLoading(true)
    try {
      await onToggle(device.deviceId, !device.hasAccess)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className={`flex items-center justify-between p-3 bg-white border rounded-md transition-colors ${
        isDisabled
          ? 'border-gray-100 opacity-60'
          : 'border-gray-200 hover:border-green-500'
      }`}
    >
      <div className="flex items-center space-x-3 flex-1">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={device.hasAccess}
          disabled={isDisabled || isLoading}
          onChange={handleToggle}
          className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500 disabled:opacity-50"
          aria-label={`Grant access to ${device.deviceName}`}
        />

        {/* Device Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">{device.deviceName}</span>
            {device.hasAccess && device.accessLevel && (
              <Badge variant="default" size="sm">
                {device.accessLevel === 'VIEW_ONLY' ? 'View Only' : 'Control'}
              </Badge>
            )}
          </div>

          {(device.location || device.wisolId) && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {device.location && <span>{device.location}</span>}
              {device.location && device.wisolId && <span> &bull; </span>}
              {device.wisolId && <span>{device.wisolId}</span>}
            </div>
          )}
        </div>

        {/* Loading Spinner */}
        {isLoading && <LoadingSpinner size="sm" />}
      </div>
    </div>
  )
}
